import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  getCurrentUser, 
  signOut, 
  signInWithRedirect,
  fetchUserAttributes,
  AuthUser 
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { amplifyConfig } from '../config/amplify';

interface AuthContextType {
  user: AuthUser | null;
  userEmail: string | null;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip authentication in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🔓 Development mode: Skipping authentication');
      // Set mock user for development
      setUser({ userId: 'dev-user', username: 'developer' } as AuthUser);
      setUserEmail('developer@example.com');
      setLoading(false);
      setError(null);
      return;
    }

    // Configure Amplify
    Amplify.configure(amplifyConfig);
    
    // Check current user
    checkUser();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      switch (event) {
        case 'signInWithRedirect':
          checkUser();
          break;
        case 'signInWithRedirect_failure':
          setError('로그인에 실패했습니다. @cyberagent.co.jp 이메일로만 접근 가능합니다.');
          setLoading(false);
          break;
        case 'signedOut':
          setUser(null);
          setUserEmail(null);
          break;
      }
    });

    return unsubscribe;
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      
      // Get user attributes
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || null);
      } catch (attrError) {
        // External provider users may fail fetchUserAttributes
        // But if getCurrentUser succeeded, we trust they're authorized
        console.log('Using external provider flow');
        // Try to get email from the current session
        // For now, we'll show them as logged in without email
        setUserEmail(null);
      }
      
      setError(null);
      
      // Clean up URL after successful login
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (err) {
      console.log('No authenticated user');
      setUser(null);
      setUserEmail(null);
      
      // Check for error in URL params (from Cognito redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const errorParam = urlParams.get('error_description');
      if (errorParam) {
        setError(decodeURIComponent(errorParam));
      }
    } finally {
      setLoading(false);
    }
  };

  const signIn = async () => {
    // Skip sign in for development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🔓 Development mode: Skipping sign in');
      return;
    }
    
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      console.error('Sign in error:', err);
      setError('로그인 중 오류가 발생했습니다.');
    }
  };

  const signOutUser = async () => {
    // Handle sign out for development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🔓 Development mode: Simulating sign out');
      setUser(null);
      setUserEmail(null);
      setError(null);
      // Simulate re-login in dev mode
      setTimeout(() => {
        setUser({ userId: 'dev-user', username: 'developer' } as AuthUser);
        setUserEmail('developer@example.com');
      }, 100);
      return;
    }
    
    try {
      await signOut();
      setUser(null);
      setUserEmail(null);
      setError(null);
    } catch (err) {
      console.error('Sign out error:', err);
      setError('로그아웃 중 오류가 발생했습니다.');
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      userEmail,
      loading,
      error,
      signIn,
      signOut: signOutUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};